import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import styles from './styles.module.scss';
import {
  MetricChartDotRadius,
  MetricChartDotSmall,
  MetricChartXAxisHeight,
  MetricChartYAxisWidth,
} from './constants/index.jsx';
import { BreakpointMobile } from '../../constants/ui';
import { MetricChartHeader, MetricChartTooltip } from './index.jsx';
import { ChartColorsMap } from '../Charts/constants/index.jsx';

const ChartTooltip = ({ active, payload, sensor }) => {
  if (!active || !payload || payload.length < 1) return null;
  const data = payload[0].payload;
  const t = Math.round(data.value);
  const tMax = Math.round(data.area[1]);
  const tMin = Math.round(data.area[0]);
  const valueCompared =
    payload.length > 2
      ? [
          {
            name: sensor.name,
            value: `${data.value}°C`,
            color: ChartColorsMap.temperature,
          },
          {
            name: sensor.name2,
            value: `${data.value2}°C`,
            color: ChartColorsMap.temperatureCompared,
          },
        ]
      : null;
  return (
    t !== null && (
      <MetricChartTooltip
        value={`${t > 0 ? '+' + t : t}°C Avg`}
        label={`${tMin > 0 ? '+' + tMin : tMin}...${tMax > 0 ? '+' + tMax : tMax}°C`}
        comparedData={valueCompared}
      />
    )
  );
};

const MetricChartTemperature = ({ title, data, crop, sensor, widthViewport }) => {
  const { crops } = useSelector((state) => state.catalog);
  const { selectedSecondarySensor } = useSelector((state) => state.sensors);
  const [thresholdOffset, setThresholdOffset] = useState(null);
  const [thresholdMinY, setThresholdMinY] = useState(null);
  const [thresholdMaxY, setThresholdMaxY] = useState(null);

  const [domainYAsis, setDomainYAsis] = useState(null);
  const [ticksYAsis, setTicksYAsis] = useState(null);

  useEffect(() => {
    if (data) {
      const domain = data.domain;
      if (domain && domain.length > 1) {
        setDomainYAsis(data.domain);
        setTicksYAsis([data.domain[0], Math.round((data.domain[1] + data.domain[0]) / 2), data.domain[1]]);
      }

      let thresholdMin = null;
      let thresholdMax = null;

      const rangeData = data.data;

      if (crops && crop) {
        const cropTemp = crops.find((c) => c.id === crop);
        if (cropTemp) {
          const values = rangeData.map((d) => d.value);
          const cropTMin = Math.round(cropTemp.temperature_min);
          const cropTMax = Math.round(cropTemp.temperature_max);

          if (values && values.length > 0) {
            // Gradient of line depending on crop
            thresholdMin = Math.min(...values);
            thresholdMax = Math.max(...values);
            const percent = (val) => (val - thresholdMin) / (thresholdMax - thresholdMin);
            const t = 100 - percent(cropTMin) * 100;
            setThresholdOffset(t > 100 ? 100 : t < 0 ? 0 : Math.round(t));
          }
          setThresholdMinY(cropTMin);
          setThresholdMaxY(cropTMax);
        }
      } else {
        setThresholdOffset(null);
      }
    }
  }, [crops, crop, data]);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.metricChart}>
      {widthViewport > BreakpointMobile && (
        <MetricChartHeader
          title={title}
          sensorName={sensor.name}
          sensorNameCompared={sensor.name2}
          color={ChartColorsMap.temperature}
          colorCompared={ChartColorsMap.temperatureCompared}
          widthViewport={widthViewport}
        />
      )}

      <ResponsiveContainer width="100%" height={140} className="chart-report">
        <ComposedChart
          width={500}
          height={140}
          data={data.data}
          margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="gradientTemperature" x1="0" y1="0" x2="0" y2="1">
              {thresholdOffset === 0 && (
                <>
                  <stop offset="0%" stopColor={ChartColorsMap.temperatureMin} stopOpacity={1} />
                  <stop offset="100%" stopColor={ChartColorsMap.temperatureMin} stopOpacity={1} />
                </>
              )}
              {thresholdOffset === 100 && (
                <>
                  <stop offset="0%" stopColor={ChartColorsMap.temperatureMax} stopOpacity={1} />
                  <stop offset="100%" stopColor={ChartColorsMap.temperature} stopOpacity={1} />
                </>
              )}
              {thresholdOffset > 0 && thresholdOffset < 100 && (
                <>
                  <stop
                    offset={`${thresholdOffset > 15 ? thresholdOffset - 10 : thresholdOffset}%`} // плавный переход между цветами
                    stopColor={ChartColorsMap.temperature}
                    stopOpacity={1}
                  />
                  <stop offset={`${thresholdOffset}%`} stopColor={ChartColorsMap.temperatureMin} stopOpacity={1} />
                  <stop offset="100%" stopColor={ChartColorsMap.temperatureMin} stopOpacity={1} />
                </>
              )}
            </linearGradient>
          </defs>
          <Area
            dataKey="area"
            type="monotone"
            stroke="none"
            fillOpacity={0.03}
            fill="#000"
            activeDot={MetricChartDotSmall}
          />
          <CartesianGrid strokeDasharray="0 0" stroke={'var(--neutral-8)'} vertical={false} />
          {((!selectedSecondarySensor && thresholdMaxY) || thresholdMaxY === 0) && (
            <ReferenceLine y={Number(thresholdMaxY)} stroke="#FFA39E" strokeDasharray="2 2" />
          )}
          {((!selectedSecondarySensor && thresholdMinY) || thresholdMinY === 0) && (
            <ReferenceLine y={Number(thresholdMinY)} stroke="#1890FF" strokeDasharray="2 2" />
          )}
          <XAxis dataKey="date" tickLine={false} height={MetricChartXAxisHeight} />
          <YAxis
            domain={domainYAsis}
            ticks={ticksYAsis}
            tickCount={3}
            tickSize={0}
            tickMargin={6}
            width={MetricChartYAxisWidth}
            tickFormatter={(value) => (value > 0 ? `+${value}` : `${value}`)}
          />
          <Tooltip content={<ChartTooltip sensor={sensor} />} />
          <Line
            type="monotone"
            dataKey="value"
            strokeWidth={2}
            stroke={
              !selectedSecondarySensor && thresholdOffset !== null
                ? 'url(#gradientTemperature)'
                : ChartColorsMap.temperature
            }
            activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.temperature }}
          />
          {selectedSecondarySensor && data.value2 !== null && (
            <>
              <Area
                dataKey="area2"
                type="monotone"
                stroke="none"
                fillOpacity={0.03}
                fill="#000"
                activeDot={MetricChartDotSmall}
              />
              <Line
                type="monotone"
                dataKey="value2"
                strokeWidth={2}
                stroke={ChartColorsMap.temperatureCompared}
                activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.temperatureCompared }}
                strokeDasharray="2 2"
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <div className={styles.metricChartUnit}>°C</div>
    </div>
  );
};

export default MetricChartTemperature;
